import { parseReplacementWithPauses } from './pause-tags';

/** The minimum a word needs to expose to be matched: its text. */
export type MatchableWord = { word: string };

/**
 * The minimum an interleaved (word + pause) token needs to expose to be matched: its text and
 * whether it is a pause. Pauses are matched presence-only (their length is ignored), like
 * edit_ai_voiceover, so `InterleavedWord` satisfies this directly.
 */
export type MatchableToken = { word: string; isPause?: boolean };

export type WordSequenceMatch = { startIndex: number; endIndex: number };

/**
 * Normalize a transcript token for tolerant matching: lowercase and strip leading/trailing
 * punctuation and quotes, so "Editorial.", "editorial" and "“editorial”" all compare
 * equal. Internal characters are kept (so "don't" and "state-of-the-art" survive intact). A token
 * that is nothing but punctuation (e.g. a lone ".") normalizes to an empty string.
 */
export function normalizeTranscriptToken(token: string): string {
    return token.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

/**
 * Find EVERY place a sequence of requested words occurs in a transcript, tolerant of
 * punctuation, quotes, case, and whether the caller passed the phrase as a single string or as
 * separate tokens. Punctuation-only tokens on either side are ignored. Each match is the
 * ORIGINAL transcript indices of its first and last matched words.
 *
 * Callers that need a single result use findWordSequenceIndices; callers that must detect
 * ambiguity (e.g. edit_ai_voiceover rejecting a phrase that appears more than once) use this.
 */
export function findAllWordSequenceMatches(
    transcriptWords: MatchableWord[],
    requestedWords: string[],
): WordSequenceMatch[] {
    // Re-tokenize the request (a phrase passed as one element becomes its words), normalize,
    // and drop punctuation-only tokens.
    const normalizedRequest = requestedWords
        .flatMap((word) => word.split(/\s+/))
        .map(normalizeTranscriptToken)
        .filter((token) => token !== '');
    if (normalizedRequest.length === 0) {
        return [];
    }

    // Normalized transcript tokens, dropping punctuation-only tokens but keeping a map back to
    // their original indices.
    const normalizedTranscript = transcriptWords
        .map((word, index) => ({ norm: normalizeTranscriptToken(word.word), index }))
        .filter((token) => token.norm !== '');

    const matches: WordSequenceMatch[] = [];
    for (let i = 0; i <= normalizedTranscript.length - normalizedRequest.length; i++) {
        let match = true;
        for (let j = 0; j < normalizedRequest.length; j++) {
            if (normalizedTranscript[i + j].norm !== normalizedRequest[j]) {
                match = false;
                break;
            }
        }
        if (match) {
            matches.push({
                startIndex: normalizedTranscript[i].index,
                endIndex: normalizedTranscript[i + normalizedRequest.length - 1].index,
            });
        }
    }
    return matches;
}

/**
 * Find where a sequence of requested words first occurs in a transcript, or null if the
 * normalized sequence does not appear consecutively.
 *
 * Shared by the word-driven transcript tools (split_scene_group, add_voiceover_scene) so an agent that
 * spells punctuation slightly differently than the transcript stored it ("editorial." vs
 * "editorial", a stray "." token, curly vs straight quotes) still lands the right words.
 */
export function findWordSequenceIndices(
    transcriptWords: MatchableWord[],
    requestedWords: string[],
): WordSequenceMatch | null {
    return findAllWordSequenceMatches(transcriptWords, requestedWords)[0] ?? null;
}

/** A token in a requested phrase's pattern. Pauses are presence-only (their N is ignored). */
type PatternToken = { kind: 'word'; text: string } | { kind: 'pause' };

/**
 * Parse a requested phrase (which may contain `<pause seconds="N"/>` tags) into an ordered pattern
 * of word and pause tokens. Punctuation-only words are dropped (like the word-only matcher), so
 * normalization tolerance is identical everywhere; each pause tag becomes one presence-only pause
 * token positioned among the words exactly where it was written.
 */
function buildRequestedPattern(requestedWords: string[]): PatternToken[] {
    const { words, pauses } = parseReplacementWithPauses(requestedWords.join(' '));
    const tokens: PatternToken[] = [];
    for (let i = 0; i <= words.length; i++) {
        for (const pause of pauses) {
            if (pause.afterWordIndex === i) {
                tokens.push({ kind: 'pause' });
            }
        }
        if (i < words.length && normalizeTranscriptToken(words[i]) !== '') {
            tokens.push({ kind: 'word', text: words[i] });
        }
    }
    return tokens;
}

function interleavedTokenMatches(token: MatchableToken, pattern: PatternToken): boolean {
    if (pattern.kind === 'pause') {
        // Presence-only: a pattern pause matches any pause token, ignoring its length.
        return !!token.isPause;
    }
    if (token.isPause) {
        return false;
    }
    return normalizeTranscriptToken(token.word) === normalizeTranscriptToken(pattern.text);
}

/**
 * Find EVERY place a requested phrase occurs in the canonical interleaved (word + pause) token
 * list as a strict, literal run — the pause-aware counterpart of findAllWordSequenceMatches.
 * Words match words with the usual punctuation/case tolerance (via normalizeTranscriptToken);
 * a `<pause>` in the request matches a pause token, presence-only.
 *
 * The match is STRICT over pauses, mirroring edit_ai_voiceover: a pause token that falls inside the
 * requested span but is left out of the request breaks the consecutive run, so it simply is not
 * found (a loud failure) rather than being silently swallowed. Punctuation-only transcript tokens
 * stay transparent (dropped on both sides), so a request with no pauses over a pause-less span
 * behaves exactly as the word-only matcher does. Returned indices are ORIGINAL indices into
 * `tokens`, so callers can read each matched token's startTime/endTime.
 */
export function findAllInterleavedMatches(
    tokens: MatchableToken[],
    requestedWords: string[],
): WordSequenceMatch[] {
    const pattern = buildRequestedPattern(requestedWords);
    if (pattern.length === 0) {
        return [];
    }

    // Keep pauses and non-punctuation words, remembering each kept token's original index.
    const kept = tokens
        .map((token, index) => ({ token, index }))
        .filter(({ token }) => token.isPause || normalizeTranscriptToken(token.word) !== '');

    const matches: WordSequenceMatch[] = [];
    for (let i = 0; i <= kept.length - pattern.length; i++) {
        let matched = true;
        for (let j = 0; j < pattern.length; j++) {
            if (!interleavedTokenMatches(kept[i + j].token, pattern[j])) {
                matched = false;
                break;
            }
        }
        if (matched) {
            matches.push({
                startIndex: kept[i].index,
                endIndex: kept[i + pattern.length - 1].index,
            });
        }
    }
    return matches;
}

/**
 * Find where a requested phrase (words, optionally with `<pause>` tags) first occurs in the
 * canonical interleaved token list, or null if it does not appear consecutively. The pause-aware
 * counterpart of findWordSequenceIndices, shared by the word-driven placement tools so they match
 * against exactly the transcript the agent reads.
 */
export function findInterleavedSequenceMatch(
    tokens: MatchableToken[],
    requestedWords: string[],
): WordSequenceMatch | null {
    return findAllInterleavedMatches(tokens, requestedWords)[0] ?? null;
}
